import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@arcadeai/design-system/components/ui/atoms/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@arcadeai/design-system/components/ui/atoms/tooltip";
import { ArrowRight, Check, Loader2, X } from "lucide-react";
import { useState } from "react";
import {
  useAcceptInvitation,
  useRejectInvitation,
} from "@/app/invitations/api/hooks";
import { useAcceptAndSwitch } from "@/app/invitations/hooks/use-accept-and-switch";
import type { GroupedInvitation } from "../../types";

type MyInvitationsActionsProps = {
  invitation: GroupedInvitation;
};

export function MyInvitationsActions({
  invitation,
}: MyInvitationsActionsProps) {
  const [isDeclineOpen, setIsDeclineOpen] = useState(false);

  const { mutate: acceptInvitation, isPending: isAccepting } =
    useAcceptInvitation();
  const { mutate: declineInvitation, isPending: isRejecting } =
    useRejectInvitation();
  const { acceptAndSwitch, isPending: isAcceptingAndSwitching } =
    useAcceptAndSwitch();

  const isAnyLoading = isAccepting || isAcceptingAndSwitching || isRejecting;

  const primaryInvitationId = invitation.primary_invitation_id;
  const canInteract = Boolean(primaryInvitationId);

  const handleAccept = () => {
    if (!primaryInvitationId) {
      return;
    }
    acceptInvitation({ invitationId: primaryInvitationId });
  };

  const handleAcceptAndSwitch = () => {
    if (!primaryInvitationId) {
      return;
    }
    acceptAndSwitch(invitation);
  };

  const handleDecline = () => {
    if (!primaryInvitationId) {
      return;
    }
    declineInvitation({ invitationId: primaryInvitationId });
    setIsDeclineOpen(false);
  };

  return (
    <div className="flex items-center justify-end gap-2">
      {/* Decline */}
      <Popover onOpenChange={setIsDeclineOpen} open={isDeclineOpen}>
        <PopoverTrigger
          render={
            <Button
              className="text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
              disabled={!canInteract || isAnyLoading}
              size="sm"
              title="Decline"
              type="button"
              variant="ghost"
            />
          }
        >
          {isRejecting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <X className="h-4 w-4" />
          )}
          <span className="ml-1 hidden xl:inline">Decline</span>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-72">
          <p className="mb-3 text-sm">
            Decline invitation to{" "}
            <span className="font-medium">{invitation.project_name}</span>? This
            cannot be undone.
          </p>
          <div className="flex justify-end gap-2">
            <Button
              onClick={() => setIsDeclineOpen(false)}
              size="sm"
              type="button"
              variant="ghost"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDecline}
              size="sm"
              type="button"
              variant="destructive"
            >
              Decline
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      {/* Accept */}
      <Tooltip>
        <TooltipTrigger
          render={
            <Button
              disabled={!canInteract || isAnyLoading}
              onClick={handleAccept}
              size="sm"
              type="button"
              variant="outline"
            />
          }
        >
          {isAccepting ? (
            <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
          ) : (
            <Check className="mr-1 h-3.5 w-3.5" />
          )}
          Accept
        </TooltipTrigger>
        <TooltipContent>Accept and stay on current project</TooltipContent>
      </Tooltip>

      {/* Join & Switch */}
      <Tooltip>
        <TooltipTrigger
          render={
            <Button
              className="bg-green-600 text-white hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-800"
              disabled={!canInteract || isAnyLoading}
              onClick={handleAcceptAndSwitch}
              size="sm"
              type="button"
            />
          }
        >
          {isAcceptingAndSwitching ? (
            <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
          ) : (
            <ArrowRight className="mr-1 h-3.5 w-3.5" />
          )}
          <span className="hidden sm:inline">Join &amp; Switch</span>
          <span className="sm:hidden">Join</span>
        </TooltipTrigger>
        <TooltipContent>
          Accept and switch to {invitation.project_name}
        </TooltipContent>
      </Tooltip>
    </div>
  );
}
